import { type ReactElement, useState, useCallback, useEffect } from 'react';
import { NeonDateField, NeonRadioField, NeonTimeField } from '@ps-refarch-ux/neon';
import type { RadioOption } from '@ps-refarch-ux/neon';
import type { Time } from '@ps-refarch-ux/mfe-utils';
import { plansOfStudyRepository } from '@/repositories';
import { normalizeCheckedSchoolNidsForPublishAndUpdate } from '@/utils/plan-publish-and-update';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import type { School } from '@/types/school';
import { isPosReadOnly } from '@/utils/cp-context';
import { SchoolsDrawer } from './schools-drawer';
import {
  PLAN_SCHOOLS_MAPPING_COPY,
  buildPublishedSchoolsSaveErrorHeading,
  getSchoolsListAndSelectionForMappingMode,
} from './plan-schools-mapping';
import {
  buildNoSchoolsSelectedForPublishToast,
  buildPublishImmediateSuccessToastMessage,
  type SchedulePublishSuccessPayload,
} from './publish-schedule-copy';
import {
  combineLocalDateAndTime,
  defaultScheduleTimeForLocalDate,
  startOfLocalDay,
  validateScheduleDateTime,
} from './publish-and-update-schedule-validation';
import styles from './publish-and-update-drawer.module.scss';

const AVAILABILITY_PUBLISH_NOW = 'now';
const AVAILABILITY_SCHEDULE = 'later';

const AVAILABILITY_OPTIONS: RadioOption[] = [
  {
    id: 'pos-publish-availability-now',
    value: AVAILABILITY_PUBLISH_NOW,
    labelText: 'Publish now',
    helperText: 'Students can review these updates immediately.',
  },
  {
    id: 'pos-publish-availability-later',
    value: AVAILABILITY_SCHEDULE,
    labelText: 'Schedule for later',
    helperText:
      'Students can review these updates at the scheduled date and time. The plan remains locked until then.',
  },
];

function toIsoDateOnly(d: Date): string {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Neon parses `data-min-value` with `new Date(...)`. A date-only `YYYY-MM-DD` is **UTC** midnight,
 * so local “today” at 00:00 can appear before the min in ahead-of-UTC zones. `YYYY-MM-DDTHH:mm:ss`
 * without a zone is interpreted as local time.
 */
function toNeonDateMinAttribute(d: Date): string {
  return `${toIsoDateOnly(d)}T00:00:00`;
}

export interface PublishAndUpdateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planName: string;
  publishedSchools: Array<{ nid: string }>;
  activeSchools: Array<{ nid: string }>;
  allSchoolsSorted: School[];
  isSchoolsLoading?: boolean;
  /**
   * When false, hides “Publish now / Schedule” radios; footer is always **Publish** and uses the
   * same publish+ingest API as Publish now (no client-only schedule).
   */
  publishScheduleEnabled: boolean;
  /** After a successful **Schedule** mutation; includes `scheduleId` for cancel via data-ingest. */
  onSchedulePublish?: (payload: SchedulePublishSuccessPayload) => void;
  /** View-only published schools (district school staff): no publish/update controls or availability section. */
  readOnly?: boolean;
}

export function PublishAndUpdateDrawer({
  isOpen,
  onClose,
  planId,
  planName,
  publishedSchools,
  activeSchools,
  allSchoolsSorted,
  isSchoolsLoading = false,
  publishScheduleEnabled,
  onSchedulePublish,
  readOnly,
}: PublishAndUpdateDrawerProps): ReactElement | null {
  const publishViewOnly = readOnly ?? isPosReadOnly();
  const publishMutation = plansOfStudyRepository.usePublishAndUpdatePlanOfStudy();
  const { showToast } = useToastNotifications();

  const [availability, setAvailability] = useState<string>(AVAILABILITY_PUBLISH_NOW);
  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfLocalDay(new Date()));
  const [timeValue, setTimeValue] = useState<Time | undefined>(() =>
    defaultScheduleTimeForLocalDate(startOfLocalDay(new Date()))
  );
  const [timeError, setTimeError] = useState<string>('');

  const { allSchools, initialSelectedNids, lockedNids } = getSchoolsListAndSelectionForMappingMode(
    'published',
    publishedSchools,
    activeSchools,
    allSchoolsSorted
  );

  const publishedCopy = PLAN_SCHOOLS_MAPPING_COPY.published;

  useEffect(() => {
    if (!isOpen) {
      setAvailability(AVAILABILITY_PUBLISH_NOW);
      const today = startOfLocalDay(new Date());
      setSelectedDate(today);
      setTimeValue(defaultScheduleTimeForLocalDate(today));
      setTimeError('');
      return;
    }
    if (!publishScheduleEnabled) {
      setAvailability(AVAILABILITY_PUBLISH_NOW);
    }
  }, [isOpen, publishScheduleEnabled]);

  const handlePrimary = useCallback(
    (checkedSchoolNids: string[]) => {
      if (publishViewOnly) {
        return;
      }
      const schoolNids = normalizeCheckedSchoolNidsForPublishAndUpdate(checkedSchoolNids);
      if (schoolNids.length === 0) {
        showToast('error', buildNoSchoolsSelectedForPublishToast());
        return;
      }

      if (publishScheduleEnabled && availability === AVAILABILITY_SCHEDULE) {
        const v = validateScheduleDateTime(selectedDate, timeValue);
        if (!v.ok) {
          setTimeError(v.timeError);
          return;
        }
        const scheduled = combineLocalDateAndTime(selectedDate, timeValue!);
        onClose();
        publishMutation.mutate(
          {
            planId,
            checkedSchoolNids: schoolNids,
            mode: 'SCHEDULED',
            scheduledAt: scheduled.toISOString(),
          },
          {
            onSuccess: (data) => {
              if (data?.status === 'SCHEDULED') {
                onSchedulePublish?.({
                  planId,
                  planName,
                  scheduledAt: scheduled,
                  schoolCount: schoolNids.length,
                  scheduleId: data.scheduleId,
                });
              }
            },
            onError: () => {
              showToast('error', {
                heading: buildPublishedSchoolsSaveErrorHeading(planName),
              });
            },
          }
        );
        return;
      }

      onClose();
      publishMutation.mutate(
        {
          planId,
          checkedSchoolNids: schoolNids,
          mode: 'IMMEDIATE',
        },
        {
          onSuccess: () => {
            showToast('info', {
              ...buildPublishImmediateSuccessToastMessage(planName),
            });
          },
          onError: () => {
            showToast('error', {
              heading: buildPublishedSchoolsSaveErrorHeading(planName),
            });
          },
        }
      );
    },
    [
      availability,
      onClose,
      onSchedulePublish,
      planId,
      planName,
      publishMutation,
      publishScheduleEnabled,
      selectedDate,
      showToast,
      timeValue,
      publishViewOnly,
    ]
  );

  if (!isOpen) {
    return null;
  }

  const drawerTitle = publishViewOnly ? publishedCopy.title : 'Publish and update plan';
  const drawerDescription = publishedCopy.description;

  const primaryLabel =
    publishScheduleEnabled && availability === AVAILABILITY_SCHEDULE ? 'Schedule' : 'Publish';

  const availabilitySection = (
    <>
      <div className={styles.availabilityRadio}>
        <p className={styles.availabilityHeading} id="pos-publish-availability-heading">
          Availability
        </p>
        <NeonRadioField
          id="pos-publish-availability"
          dataLabelText="Availability"
          dataShowLabel={false}
          dataAriaLabel="Availability"
          modelValue={availability}
          modelValueChange={(value) => {
            setAvailability(typeof value === 'string' ? value : AVAILABILITY_PUBLISH_NOW);
            setTimeError('');
          }}
          options={AVAILABILITY_OPTIONS}
          dataControlLayout="column"
        />
      </div>

      {availability === AVAILABILITY_SCHEDULE && (
        <div className={styles.scheduleFields}>
          <div className={`${styles.fieldBlock} ${styles.dateFieldWrap}`}>
            <NeonDateField
              id="pos-schedule-date"
              className={styles.dateField}
              dataLabelText="Date"
              dataShowLabel={true}
              dataPlaceholderText="dd/mm/yyyy"
              dataMinValue={toNeonDateMinAttribute(new Date())}
              modelValue={selectedDate}
              modelValueChange={(d) => {
                if (d) {
                  const day = startOfLocalDay(d);
                  setSelectedDate(day);
                  setTimeValue((prev) => {
                    const v = validateScheduleDateTime(day, prev);
                    if (v.ok) {
                      return prev;
                    }
                    return defaultScheduleTimeForLocalDate(day);
                  });
                  setTimeError('');
                }
              }}
            />
          </div>
          <div className={`${styles.fieldBlock} ${styles.timeFieldWrap}`}>
            <NeonTimeField
              id="pos-schedule-time"
              className={styles.timeField}
              dataLabelText="Time (local)"
              dataShowLabel={true}
              modelValue={timeValue}
              modelValueChange={(v) => {
                setTimeValue(v);
                setTimeError('');
              }}
            />
            {timeError ? (
              <span
                className={styles.inlineError}
                role="alert"
                data-testid="pos-schedule-time-error"
              >
                {timeError}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </>
  );

  return (
    <SchoolsDrawer
      key={`publish-update-${planId}-${isOpen}`}
      id="pos-publish-and-update-drawer"
      mode="published"
      title={drawerTitle}
      planName={planName}
      description={drawerDescription}
      showPlanNameHeading={publishViewOnly}
      allSchools={allSchools}
      initialSelectedNids={initialSelectedNids}
      lockedNids={lockedNids}
      isSchoolsLoading={isSchoolsLoading}
      readOnly={readOnly}
      onClose={onClose}
      onPrimary={publishViewOnly ? () => {} : handlePrimary}
      bodyExtension={
        !publishViewOnly && publishScheduleEnabled ? (
          <div className={styles.availabilityWrap}>{availabilitySection}</div>
        ) : null
      }
      primaryButtonText={primaryLabel}
      disablePrimaryWhenSelectionUnchanged={false}
    />
  );
}
